import _ from "lodash";
import React, { Fragment } from "react";
import { Button, Radio, Collapse, Tag, Card } from "antd";
import { PROPERTY_KEYS } from "../../lib/constants";
import PROPERTIES_MAP from "../../lib/properties";
import { cleanElementName, splitElementKey } from "../../lib/helpers";
import { useDispatch, useSelector } from "react-redux";
import {
  applyNextElementVariant,
  resetElement,
  saveElementVariant,
  setActiveSidebarPanels,
  setPropertyType,
} from "../../services/store";
import Property from "./Property";

const PropertiesPanel = ({
  selectedElement,
  handlePropertyChange,
  isGlobal,
}) => {
  const dispatch = useDispatch();
  const {
    localProperties,
    globalProperties,
    propertyType,
    layoutConfig = {},
    activeSidebarPanels,
    domOutline,
  } = useSelector((state) => state.grid);

  const { element } = splitElementKey(selectedElement);

  const getProperties = (propertyNames) => {
    const properties = propertyNames
      .map((key) => PROPERTIES_MAP[key])
      .filter((property) => {
        if (property.dependsOn) {
          const isAllDependenciesMet = property.dependsOn.every(
            ([dependencyKey, ignoreList]) => {
              if (isGlobal) {
                const val = globalProperties[element]?.[dependencyKey];
                return ignoreList.includes(val) ? false : !!val;
              } else {
                const val = localProperties[element]?.[dependencyKey];
                return ignoreList.includes(val) ? false : !!val;
              }
            },
          );

          return isAllDependenciesMet;
        }
        if (property.allowedElements)
          return property.allowedElements.includes(element);

        if (property.notAllowedElements)
          return !property.notAllowedElements.includes(element);

        return true;
      });

    return (
      <div className="flex flex-col gap-4 items-stretch">
        {properties.map((property) => {
          const props = {
            selectedElement,
            isGlobal,
            globalProperties,
            localProperties,
            handlePropertyChange,
            property,
          };
          return (
            <Property
              key={`${isGlobal ? "g-" : "l-"}${property.key}`}
              {...props}
              isColor={[
                PROPERTY_KEYS.BG_COLOR,
                PROPERTY_KEYS.TEXT_COLOR,
                PROPERTY_KEYS.BORDER_COLOR,
              ].includes(property.key)}
            />
          );
        })}
      </div>
    );
  };

  const propertyGroups = [
    {
      label: "General",
      children: getProperties([
        PROPERTY_KEYS.FONT_FAMILY,
        PROPERTY_KEYS.FONT_SIZE,
        PROPERTY_KEYS.TEXT_COLOR,
        PROPERTY_KEYS.BG_COLOR,
        PROPERTY_KEYS.OVERFLOW,
      ]),
      key: "general",
    },
    {
      label: "Background",
      children: getProperties([
        PROPERTY_KEYS.BG_IMAGE,
        PROPERTY_KEYS.BG_SIZE,
        PROPERTY_KEYS.BG_REPEAT,
        PROPERTY_KEYS.BG_POSITION,
      ]),
      key: "background",
    },
    {
      label: "Layout",
      children: getProperties([
        PROPERTY_KEYS.FLEX_BASIS,
        PROPERTY_KEYS.WIDTH,
        PROPERTY_KEYS.FLEX_DIRECTION,
        PROPERTY_KEYS.ALIGN_ITEMS,
        PROPERTY_KEYS.JUSTIFY_CONTENT,
        PROPERTY_KEYS.GAP,
      ]),
      key: "layout",
    },
    {
      label: "Spacing",
      children: getProperties([PROPERTY_KEYS.PADDING, PROPERTY_KEYS.MARGIN]),
      key: "spacing",
    },
    {
      label: "Font",
      children: getProperties([
        PROPERTY_KEYS.FONT_WEIGHT,
        PROPERTY_KEYS.FONT_STYLE,
        PROPERTY_KEYS.TEXT_ALIGN,
        PROPERTY_KEYS.TEXT_TRANSFORM,
        PROPERTY_KEYS.TEXT_DECORATION,
        PROPERTY_KEYS.TEXT_DECORATION_COLOR,
        PROPERTY_KEYS.TEXT_DECORATION_THICKNESS,
      ]),
      key: "font",
    },
    {
      label: "Border",
      children: getProperties([
        PROPERTY_KEYS.BORDER,
        PROPERTY_KEYS.BORDER_RADIUS,
        PROPERTY_KEYS.BORDER_COLOR,
        PROPERTY_KEYS.BORDER_STYLE,
      ]),
      key: "border",
    },
    {
      label: "Effects",
      children: getProperties([
        PROPERTY_KEYS.BOX_SHADOW,
        PROPERTY_KEYS.TEXT_SHADOW,
        PROPERTY_KEYS.OPACITY,
        // PROPERTY_KEYS.TRANSFORM,
      ]),
      key: "effects",
    },
    // {
    //   label: "Filters",
    //   children: getProperties([PROPERTY_KEYS.BACKDROP_FILTER]),
    //   key: "filters",
    // },
  ];

  const totalSelectedElementVariants = _.get(
    layoutConfig,
    [element, "variants"],
    [],
  ).length;

  return (
    <Fragment>
      <DomOutline outline={domOutline} selectedElement={selectedElement} />
      {selectedElement ? (
        <Fragment>
          <Card className="flex flex-col items-start gap-1 mb-3">
            <label className="text-xs font-bold">
              Selected
              {totalSelectedElementVariants > 0 ? (
                <Tag
                  className="ml-2 cursor-pointer text-xs font-normal"
                  onClick={() =>
                    dispatch(applyNextElementVariant(selectedElement))
                  }
                >{`${totalSelectedElementVariants} variant(s)`}</Tag>
              ) : null}
            </label>

            <h3 className="text-base font-bold text-left break-all w-full">
              {selectedElement}
            </h3>

            {/* <div className="flex gap-2 w-full mt-2">
              <Button onClick={() => dispatch(resetElement(selectedElement))}>
                Reset
              </Button>
              <Button
                onClick={() => dispatch(saveElementVariant(selectedElement))}
              >
                Save
              </Button>
            </div> */}
          </Card>

          <div className="flex flex-col items-start gap-1">
            <div className="flex items-center justify-between w-full">
              <label className="text-xs font-bold">Properties</label>
              <Radio.Group
                size="small"
                optionType="button"
                buttonStyle="solid"
                options={[
                  {
                    label: "Global",
                    value: "global",
                  },
                  {
                    label: "Local",
                    value: "local",
                  },
                ]}
                onChange={(e) => dispatch(setPropertyType(e.target.value))}
                value={propertyType}
              />
            </div>
            <div className="w-full mt-2">
              <Collapse
                onChange={(panels) => dispatch(setActiveSidebarPanels(panels))}
                items={propertyGroups.filter(
                  (group) => group.children.props.children.length > 0,
                )}
                activeKey={activeSidebarPanels}
                size="small"
                bordered={false}
              />
            </div>
          </div>
        </Fragment>
      ) : (
        <div className="text-xs font-bold p-2 border border-gray-200 rounded">
          No element selected
        </div>
      )}
    </Fragment>
  );
};

const DomOutline = ({ outline, selectedElement }) => {
  const selectedElementKey = splitElementKey(selectedElement).element;

  const boxClasses = `p-1.5 border flex flex-col gap-1.5 w-full rounded`;
  const generateLayer = (_list, idx) => {
    return _list.map(({ element = {}, children }, i) => {
      const { elementName } = element || {};
      const isSelected =
        cleanElementName(elementName || "") ===
        cleanElementName(selectedElementKey || "");

      // If elementName is "-" it means it's a wrapper div we added around elements that don't have a unique selector. We don't want to show these in the outline, but we want to show their children.
      if (elementName === "-") return generateLayer(children, idx + 1);

      return (
        <div
          key={`${elementName}-${idx}.${i}`}
          className={`${boxClasses} ${isSelected ? "border-blue-400 bg-blue-200 font-bold" : "border-white-200 bg-white"}`}
        >
          <span className="text-[10px]">{elementName}</span>
          {children && generateLayer(children, idx + 1)}
        </div>
      );
    });
  };

  return (
    <div className={`${boxClasses} mb-2`}>{generateLayer(outline, 0)}</div>
  );
};

export default PropertiesPanel;
